import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format, formatDuration, formatISO, intervalToDuration, parseISO } from 'date-fns'
import {
  Currency,
  ModelTypes,
  TortoiseContribPydanticCreatorModelsPricesLeaf as Price,
} from '../api/topli/TopliApi'
import { SelectOption } from '../ui/Select/Select'
import {
  LanguageCode,
  languages,
  ShortLanguageCode,
} from '@/features/MultilingualTabs/model/config'
import {
  CreateTranslations,
  EditTranslations,
  TranslationLeaf,
} from '@/entities/ContentTranslation/model/types'
import { LanguageTabConfig } from '@/features/MultilingualTabs/model/types'
import { enUS, ru, tr } from 'date-fns/locale'
import { TFunction } from 'i18next'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const numberWithoutNaNOrNull = (value: unknown): number | null => {
  const res = Number(value)
  return Number.isNaN(res) ? null : res
}

export const isNullOrUndefined = (k: unknown): boolean => k === null || k === undefined

export const createSelectOptions = <T extends { id: number; name: string }>(arr?: T[]) =>
  (arr || []).map(item => ({ value: item.id, label: item.name }))

export const createTranslatedSelectOptions = <
  T extends { id: number; name: string; translations: any },
>(
  arr?: T[],
) =>
  (arr || []).map(item => ({
    value: item.id,
    label:
      item.translations && item.translations.length && item.translations[0].name
        ? item.translations[0].name
        : item.name,
  }))

export const formatDateForSending = (
  date: Date | null | string,
  time: 'start' | 'end' | null = null,
): string | null => {
  if (!date) return null
  const originalDate = new Date(date)

  if (time === 'start') {
    originalDate.setHours(0, 0, 0, 0)
    return format(originalDate, 'yyyy-MM-dd:HH:mm')
  }

  if (time === 'end') {
    originalDate.setHours(23, 59, 0, 0)
    return format(originalDate, 'yyyy-MM-dd:HH:mm')
  }

  return format(originalDate, 'yyyy-MM-dd')
}

export const formatDateYearForSending = (date: Date | null | number) => {
  if (!date) return date
  return new Date(date).getFullYear()
}

export const formatDateForDateInput = (date: string | null) => {
  if (!date) return date
  const originalDate = parseISO(date)
  return formatISO(originalDate, { representation: 'complete' })
}

export const formatDateForTable = (date: Date | string | null, lang: string) => {
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }
  if (!date) return date
  return new Intl.DateTimeFormat(lang, options as object).format(new Date(date))
}

export const chooseFormat = (lang: string) => {
  if (lang === 'en-US') {
    return 'MM-DD-YYYY'
  }
  return 'DD-MM-YYYY'
}

export const formatDateWithoutTime = (date: string | null, lang: string) => {
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
  if (!date) return date
  const parsedDate = parseISO(date)
  return new Intl.DateTimeFormat(lang, options as object).format(parsedDate)
}

type FormatDateOptions = {
  showDay?: boolean
}

export const formatDateForChart = (
  date: string | Date,
  lang: string,
  { showDay = true }: FormatDateOptions = {},
): string => {
  if (!date) return ''
  const d = typeof date === 'string' ? new Date(date) : date

  let pattern: string

  if (lang === 'en-US') {
    pattern = showDay ? 'MM.dd.yyyy' : 'MM.yyyy'
  } else {
    pattern = showDay ? 'dd.MM.yyyy' : 'MM.yyyy'
  }

  return format(d, pattern)
}

const localeMap: Record<string, Locale> = {
  'ru-RU': ru,
  'en-US': enUS,
  'tr-TR': tr,
}

export const formatDurationFromDates = (start: string, end: string, lang: string, t: TFunction) => {
  const startDate = parseISO(start)
  const endDate = parseISO(end)

  const duration = intervalToDuration({
    start: startDate,
    end: endDate,
  })

  const locale = localeMap[lang] || enUS

  const totalSeconds = Math.floor((endDate.getTime() - startDate.getTime()) / 1000)

  if (totalSeconds < 60) {
    return t('common:less_than_minute').toLowerCase()
  }

  return formatDuration(duration, {
    format: ['hours', 'minutes'],
    locale,
  })
}

export const displayPriceInLocalFormat = (amount: number, currency: string, divider: number) => {
  const price = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount / divider)
  return price
}

export const generateRandomPassword = (length: number) => {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+'
  return Array.from({ length }, () => charset[Math.floor(Math.random() * charset.length)]).join('')
}

export const convertPhoneStringToNumber = (phone: string) => {
  const numericString = phone.replace(/\D/g, '')
  return parseInt(numericString, 10)
}

export const convertPhoneNumberToString = (phone: number | null) => {
  if (Number.isNaN(phone)) return ''
  if (!phone || phone === 0) return '–'
  const numericString = phone.toString()
  const formattedString = `+${numericString.substring(0, 2)} (${numericString.substring(
    2,
    5,
  )}) ${numericString.substring(5, 8)}-${numericString.substring(8)}`
  return formattedString
}

export const createBlobFromFile = (file: File) => {
  const blob = new Blob([file], { type: file.type })
  return blob
}

export const convertBlobUrlsToBinaryFiles = async (blobUrls: string[]): Promise<Blob[]> => {
  const promises = blobUrls.map(url => fetch(url).then(response => response.blob()))
  const blobs = await Promise.all(promises)
  return blobs
}

export const isLocalPrice = (prices?: Price[]) => prices?.find(price => price.is_local)

export const isNormalPrice = (prices?: Price[]) => prices?.find(price => !price.is_local)

export function getUnitPriceData(prices: Price[], isShowLocalPrices: boolean) {
  // Filter prices that were created by default without conversion (main: true)
  const mainPrices = prices.filter(item => item.main)

  const priceData = isShowLocalPrices ? isLocalPrice(mainPrices) : isNormalPrice(mainPrices)

  const currencyCode = priceData?.currency
  const currentCurrency = currencyCode ? Currency[currencyCode] : 'usd'

  const price = priceData?.price

  return { price, currentCurrency }
}

export const displayPriceFromToInLocaleFormat = (
  prices?: any[] | null,
  currency?: Currency | null,
) => {
  const currencyFormat = currency ? String(Currency[currency]).toLowerCase() : 'usd'
  const price_min = prices ? prices[0] : 0
  const price_max = prices ? prices[1] : 0
  return `${displayPriceInLocalFormat(
    price_min,
    currencyFormat,
    100,
  )} – ${displayPriceInLocalFormat(price_max, currencyFormat, 100)}`
}

export const processCropValue = (value: number) => {
  if (!numberWithoutNaNOrNull(value)) return 0
  if (value < 0) return Math.abs(value)
  return Number(`-${value}`)
}

export const transformOptionsToTableData = <T>(options: SelectOption<T>[]) => {
  return options.map(option => ({
    id: option.value,
    name: option.label,
  }))
}

export function enumToOptions(enumObject, preserveLabelCase = false) {
  return Object.entries(enumObject)
    .filter(([key]) => isNaN(Number(key)))
    .map(([key, value]) => ({
      value: value as number,
      label: preserveLabelCase
        ? key
        : key[0].toUpperCase() +
          key
            .slice(1)
            .split(/(?=[A-Z])/)
            .map(word => word[0].toLowerCase() + word.slice(1))
            .join(' '),
    }))
}

export const findLabelByValue = (
  options: { value: number; label: string }[],
  value: number | string,
): string => {
  const option = options.find(option => option.value === value)
  return option ? option.label : ''
}

export const generateRouteKey = (str: string) => {
  return str.toLowerCase().replace(/\s+/g, '_')
}

export const getEntityType = (modelType: ModelTypes, id: number) => {
  switch (modelType) {
    case ModelTypes.Unit:
      return { unit_id: id }
    case ModelTypes.Building:
      return { building_id: id }
    case ModelTypes.Residential:
      return { residential_id: id }
    case ModelTypes.Company:
      return { company_id: id }
    case ModelTypes.ResidentialAmenity:
    case ModelTypes.UnitAmenity:
      return { amenity_id: id }
    case ModelTypes.Webinar:
      return { webinar_id: id }
  }
}

export const matchTranslations = (
  sourceTranslations: TranslationLeaf[],
  targetTranslations: EditTranslations | CreateTranslations,
): EditTranslations => {
  const updatedTranslations = JSON.parse(JSON.stringify(targetTranslations)) as EditTranslations

  sourceTranslations.forEach(sourceTranslation => {
    const { language, ...rest } = sourceTranslation

    let languageKey

    switch (language) {
      case LanguageCode.en:
        languageKey = ShortLanguageCode.en
        break
      case LanguageCode.ru:
        languageKey = ShortLanguageCode.ru
        break
      case LanguageCode.tr:
        languageKey = ShortLanguageCode.tr
        break
      case LanguageCode.fa:
        languageKey = ShortLanguageCode.fa
        break
      case LanguageCode.ar:
        languageKey = ShortLanguageCode.ar
        break
      default:
        return
    }

    if (updatedTranslations[languageKey]) {
      Object.keys(updatedTranslations[languageKey]).forEach(key => {
        if (rest.hasOwnProperty(key)) {
          updatedTranslations[languageKey][key] = rest[key]
        }
      })
    }
  })

  return updatedTranslations
}

export const getCurrentTranslation = (object: any, lang: string, value: string) => {
  const translation = object.translations?.find(item => item.language === lang)
  const translatedValue = translation?.[value]
  if (translatedValue && translatedValue.length) {
    return translatedValue
  }
  return object[value]
}

export const isMachineTranslation = (translations: TranslationLeaf[]) =>
  translations.some(item => item.ai_translated)

export const hasAiTranslation = (translations: EditTranslations | CreateTranslations): boolean => {
  return Object.values(translations).some(translation => translation.ai_translated)
}

export const createAutocompleteOptions = (
  data: any[] = [],
  language: string,
  translationKey: string = 'name',
) =>
  data.map(item => ({
    value: String(item.id),
    label: getCurrentTranslation(item, language, translationKey),
  }))

export const generateLanguageTabsConfig = (
  isLoading: boolean,
  component: any,
  entityType: 'building' | 'residential' | 'unit' | 'amenity' | 'company' | 'webinar',
): LanguageTabConfig[] =>
  languages.map(item => ({
    title: item.languageLabel,
    value: item.languageCode,
    component,
    props: {
      isLoading,
      languageCode: item.languageCode,
      required: item.required,
      isRtl: item.rtl,
      entityType,
    },
  }))

export const isQuillContentEmpty = (value?: string | null): boolean => {
  if (!value) return true

  const parser = new DOMParser()
  const doc = parser.parseFromString(value, 'text/html')
  const text = doc.body.textContent?.replace(/\s|\u00A0/g, '').trim()

  return !text
}

export const cleanQuillValue = (value?: string | null): string | null => {
  return isQuillContentEmpty(value) ? null : value?.trim() || null
}

export const createDateRangeFilter = (
  fieldName: string,
  startDate?: string | null,
  endDate?: string | null,
): Record<string, any> => {
  if (startDate && endDate) {
    return { [`${fieldName}__range`]: [startDate, endDate] }
  }
  if (startDate) {
    return { [`${fieldName}__gte`]: startDate }
  }
  if (endDate) {
    return { [`${fieldName}__lte`]: endDate }
  }
  return {}
}
